<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import BookingsEditor from "./BookingsEditor.vue";
import CurrentlyBuildingEditor from "./CurrentlyBuildingEditor.vue";
import ExperimentsEditor from "./ExperimentsEditor.vue";
import MenuEditor from "./MenuEditor.vue";
import ProjectsEditor from "./ProjectsEditor.vue";

import {
  api,
  type BusinessLocation,
  type BusinessProfile,
  type DayName,
  type DayHours,
  type LabelledValue,
  type SiteSummary,
} from "./api";

const props = defineProps<{ site: SiteSummary }>();
const emit = defineEmits<{ back: [] }>();

const days: DayName[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const profile = ref<BusinessProfile | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const notice = ref("");
const activeTab = ref("business-profile");
const previewUrl = `/api/sites/${props.site.id}/preview/`;
const enabledPlugins = computed(() =>
  props.site.availablePlugins.filter((plugin) => props.site.modules.includes(plugin.key)),
);

function hours(): Record<DayName, DayHours> {
  return Object.fromEntries(
    days.map((day) => [day, { closed: day === "sunday", open: "09:00", close: "17:00" }]),
  ) as Record<DayName, DayHours>;
}

function newLocation(): BusinessLocation {
  return {
    label: "Main location",
    addressLine1: "",
    addressLine2: "",
    townCity: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    phone: "",
    email: "",
    openingHours: hours(),
  };
}

function addContact(target: LabelledValue[], label: string) {
  target.push({ label, value: "" });
}

async function save() {
  if (!profile.value || !canEdit.value) {
    return;
  }
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    profile.value = (await api.updateBusinessProfile(props.site.id, profile.value)).profile;
    notice.value = "Business details saved and available to the website.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save business details";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await api.businessProfile(props.site.id);
    profile.value = response.profile;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load business details";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="profile-shell">
    <header>
      <div class="heading">
        <button class="back" type="button" aria-label="Back to websites" @click="emit('back')">←</button>
        <div>
          <p class="eyebrow">{{ site.tenantName }}</p>
          <h1>{{ site.name }}</h1>
        </div>
      </div>
      <div class="header-actions">
        <a class="view-site" :href="previewUrl" target="_blank" rel="noopener">
          View website <span aria-hidden="true">↗</span>
        </a>
        <button
          v-if="activeTab === 'business-profile' || activeTab === 'locations'"
          type="button"
          :disabled="saving || !canEdit"
          @click="save"
        >
          {{ saving ? "Saving…" : "Save business details" }}
        </button>
      </div>
    </header>

    <main>
      <div v-if="loading" class="loading">Loading business details…</div>
      <template v-else-if="profile">
        <p v-if="error" class="message error" role="alert">{{ error }}</p>
        <p v-if="notice" class="message success" role="status">{{ notice }}</p>
        <p v-if="!canEdit" class="message info">You have read-only access.</p>

        <nav class="manager-tabs" aria-label="Website manager sections" role="tablist">
          <button
            id="tab-business-profile"
            class="manager-tab"
            :class="{ active: activeTab === 'business-profile' }"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'business-profile'"
            aria-controls="panel-business-profile"
            @click="activeTab = 'business-profile'"
          >
            Business profile
          </button>
          <button
            id="tab-locations"
            class="manager-tab"
            :class="{ active: activeTab === 'locations' }"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'locations'"
            aria-controls="panel-locations"
            @click="activeTab = 'locations'"
          >
            Locations
            <span class="tab-count">{{ profile.locations.length }}</span>
          </button>
          <button
            v-for="plugin in enabledPlugins"
            :id="`tab-plugin-${plugin.key}`"
            :key="plugin.key"
            class="manager-tab"
            :class="{ active: activeTab === `plugin-${plugin.key}` }"
            type="button"
            role="tab"
            :aria-selected="activeTab === `plugin-${plugin.key}`"
            :aria-controls="`panel-plugin-${plugin.key}`"
            @click="activeTab = `plugin-${plugin.key}`"
          >
            {{ plugin.name }}
          </button>
        </nav>

        <div
          v-show="activeTab === 'business-profile'"
          id="panel-business-profile"
          class="tab-panel"
          role="tabpanel"
          aria-labelledby="tab-business-profile"
        >

        <section class="card">
          <div class="section-title">
            <p class="eyebrow">Company</p>
            <h2>Business profile</h2>
            <p>This information is used throughout your website.</p>
          </div>
          <label>
            Business name
            <input v-model="profile.businessName" :disabled="!canEdit" maxlength="200" required>
          </label>
          <label>
            Short description
            <textarea v-model="profile.description" :disabled="!canEdit" rows="5" maxlength="5000" />
          </label>
        </section>

        <section class="card">
          <div class="section-heading">
            <div class="section-title">
              <p class="eyebrow">Contact</p>
              <h2>Telephone numbers</h2>
            </div>
            <button
              class="secondary"
              type="button"
              :disabled="!canEdit"
              @click="addContact(profile.phoneNumbers, 'Main')"
            >
              Add number
            </button>
          </div>
          <div v-for="(phone, index) in profile.phoneNumbers" :key="index" class="contact-row">
            <input v-model="phone.label" :disabled="!canEdit" maxlength="80" placeholder="Label">
            <input v-model="phone.value" :disabled="!canEdit" maxlength="500" placeholder="Telephone number">
            <button class="remove" type="button" :disabled="!canEdit" @click="profile.phoneNumbers.splice(index, 1)">Remove</button>
          </div>
          <p v-if="!profile.phoneNumbers.length" class="empty">No telephone numbers added.</p>
        </section>

        <section class="card">
          <div class="section-heading">
            <div class="section-title">
              <p class="eyebrow">Contact</p>
              <h2>Email addresses</h2>
            </div>
            <button
              class="secondary"
              type="button"
              :disabled="!canEdit"
              @click="addContact(profile.emailAddresses, 'Main')"
            >
              Add email
            </button>
          </div>
          <div v-for="(email, index) in profile.emailAddresses" :key="index" class="contact-row">
            <input v-model="email.label" :disabled="!canEdit" maxlength="80" placeholder="Label">
            <input v-model="email.value" :disabled="!canEdit" type="email" maxlength="500" placeholder="Email address">
            <button class="remove" type="button" :disabled="!canEdit" @click="profile.emailAddresses.splice(index, 1)">Remove</button>
          </div>
          <p v-if="!profile.emailAddresses.length" class="empty">No email addresses added.</p>
        </section>
        </div>

        <div
          v-show="activeTab === 'locations'"
          id="panel-locations"
          class="tab-panel"
          role="tabpanel"
          aria-labelledby="tab-locations"
        >

        <section class="card">
          <div class="section-heading">
            <div class="section-title">
              <p class="eyebrow">Premises</p>
              <h2>Locations and opening hours</h2>
            </div>
            <button
              class="secondary"
              type="button"
              :disabled="!canEdit"
              @click="profile.locations.push(newLocation())"
            >
              Add location
            </button>
          </div>

          <article v-for="(location, locationIndex) in profile.locations" :key="locationIndex" class="location">
            <div class="location-heading">
              <h3>{{ location.label || "Location " + (locationIndex + 1) }}</h3>
              <button class="remove" type="button" :disabled="!canEdit" @click="profile.locations.splice(locationIndex, 1)">Remove location</button>
            </div>
            <div class="grid two">
              <label>Location label<input v-model="location.label" :disabled="!canEdit" maxlength="100"></label>
              <label>Address line 1<input v-model="location.addressLine1" :disabled="!canEdit" maxlength="200" required></label>
              <label>Address line 2<input v-model="location.addressLine2" :disabled="!canEdit" maxlength="200"></label>
              <label>Town or city<input v-model="location.townCity" :disabled="!canEdit" maxlength="120"></label>
              <label>County<input v-model="location.county" :disabled="!canEdit" maxlength="120"></label>
              <label>Postcode<input v-model="location.postcode" :disabled="!canEdit" maxlength="20"></label>
              <label>Location telephone<input v-model="location.phone" :disabled="!canEdit" maxlength="100"></label>
              <label>Location email<input v-model="location.email" :disabled="!canEdit" type="email" maxlength="254"></label>
            </div>

            <div class="hours">
              <h4>Opening hours</h4>
              <div v-for="day in days" :key="day" class="day-row">
                <strong>{{ day }}</strong>
                <label class="closed">
                  <input v-model="location.openingHours[day].closed" :disabled="!canEdit" type="checkbox">
                  Closed
                </label>
                <template v-if="!location.openingHours[day].closed">
                  <input v-model="location.openingHours[day].open" :disabled="!canEdit" type="time">
                  <span>to</span>
                  <input v-model="location.openingHours[day].close" :disabled="!canEdit" type="time">
                </template>
              </div>
            </div>
          </article>
          <p v-if="!profile.locations.length" class="empty">No locations added.</p>
        </section>
        </div>

        <section v-show="activeTab === 'business-profile'" class="card">
          <div class="section-heading">
            <div class="section-title">
              <p class="eyebrow">Online</p>
              <h2>Social links</h2>
            </div>
            <button
              class="secondary"
              type="button"
              :disabled="!canEdit"
              @click="addContact(profile.socialLinks, 'Facebook')"
            >
              Add link
            </button>
          </div>
          <div v-for="(social, index) in profile.socialLinks" :key="index" class="contact-row">
            <input v-model="social.label" :disabled="!canEdit" maxlength="80" placeholder="Platform">
            <input v-model="social.value" :disabled="!canEdit" maxlength="500" placeholder="https://">
            <button class="remove" type="button" :disabled="!canEdit" @click="profile.socialLinks.splice(index, 1)">Remove</button>
          </div>
        </section>

        <div
          v-for="plugin in enabledPlugins"
          v-show="activeTab === `plugin-${plugin.key}`"
          :id="`panel-plugin-${plugin.key}`"
          :key="`panel-${plugin.key}`"
          class="tab-panel"
          role="tabpanel"
          :aria-labelledby="`tab-plugin-${plugin.key}`"
        >
          <MenuEditor v-if="plugin.key === 'menu'" :site-id="site.id" />
          <BookingsEditor v-else-if="plugin.key === 'table-bookings'" :site-id="site.id" />
          <ProjectsEditor v-else-if="plugin.key === 'projects'" :site-id="site.id" />
          <CurrentlyBuildingEditor v-else-if="plugin.key === 'currently-building'" :site-id="site.id" />
          <ExperimentsEditor v-else-if="plugin.key === 'experiments'" :site-id="site.id" />
          <section v-else class="card">
            <div class="section-title">
              <p class="eyebrow">{{ site.businessType?.name }} plugin</p>
              <h2>{{ plugin.name }}</h2>
              <p>{{ plugin.description }}</p>
            </div>
            <p class="empty">This plugin does not have a manager screen yet.</p>
          </section>
        </div>

        <div
          v-if="activeTab === 'business-profile' || activeTab === 'locations'"
          class="save-bar"
        >
          <span>Changes update the business data used by your website.</span>
          <button type="button" :disabled="saving || !canEdit" @click="save">
            {{ saving ? "Saving…" : "Save business details" }}
          </button>
        </div>
      </template>
    </main>
  </div>
</template>

<style scoped>
.profile-shell { min-height: 100vh; background: #f4f6f2; }
header {
  display: flex;
  position: sticky;
  z-index: 5;
  top: 0;
  padding: 1rem 2rem;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dce2dc;
  background: rgb(255 255 255 / 94%);
  backdrop-filter: blur(12px);
}
.heading, .header-actions, .section-heading, .location-heading, .save-bar { display: flex; align-items: center; }
.heading { gap: 1rem; }
.header-actions { gap: .75rem; }
.heading h1, h2, h3, h4 { margin: 0; }
.back { width: 2.5rem; height: 2.5rem; padding: 0; color: #334b40; background: #edf1ed; }
.view-site {
  display: inline-flex;
  min-height: 2.5rem;
  padding: .65rem 1rem;
  align-items: center;
  gap: .55rem;
  border: 1px solid #c8d2cb;
  border-radius: .5rem;
  color: #29483a;
  background: white;
  font-size: .85rem;
  font-weight: 700;
  text-decoration: none;
}
.view-site:hover { border-color: #799087; background: #f6f8f6; }
.eyebrow { margin: 0 0 .25rem; color: #6b7b72; font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
main { display: grid; width: min(68rem, calc(100% - 2rem)); margin-inline: auto; padding-block: 2rem 5rem; gap: 1.25rem; }
.loading { padding: 5rem; text-align: center; }
.manager-tabs {
  display: flex;
  position: sticky;
  z-index: 4;
  top: 5.15rem;
  overflow-x: auto;
  margin: 0;
  padding: .35rem;
  gap: .25rem;
  border: 1px solid #dce2dc;
  border-radius: .8rem;
  background: rgb(255 255 255 / 96%);
  box-shadow: 0 .4rem 1.2rem rgb(32 52 43 / 6%);
}
.manager-tab {
  display: inline-flex;
  min-width: max-content;
  padding: .7rem 1rem;
  align-items: center;
  gap: .5rem;
  border-radius: .55rem;
  color: #53665c;
  background: transparent;
}
.manager-tab:hover { color: #254c3a; background: #eff3f0; }
.manager-tab.active { color: white; background: #285c47; }
.manager-tab:focus-visible { outline: 3px solid rgb(40 92 71 / 22%); outline-offset: 2px; }
.tab-count {
  min-width: 1.3rem;
  padding: .1rem .35rem;
  border-radius: 999px;
  color: #53665c;
  background: #e3ebe6;
  font-size: .7rem;
}
.manager-tab.active .tab-count { color: #285c47; background: white; }
.tab-panel { display: grid; gap: 1.25rem; }
.card { display: grid; gap: 1.15rem; padding: 1.5rem; border: 1px solid #dce2dc; border-radius: 1rem; background: white; }
.section-title p:last-child { margin: .45rem 0 0; color: #6d7972; }
.section-heading, .location-heading, .save-bar { justify-content: space-between; gap: 1rem; }
.secondary { border: 1px solid #c8d2cb; color: #29483a; background: white; }
label { display: grid; gap: .4rem; }
textarea { width: 100%; resize: vertical; border: 1px solid #cbd5ce; border-radius: .65rem; padding: .8rem; font: inherit; }
.contact-row { display: grid; gap: .75rem; grid-template-columns: 10rem 1fr auto; }
.remove { color: #91382f; background: #fae9e7; }
.empty { margin: 0; color: #758078; }
.location { display: grid; gap: 1.25rem; padding: 1.25rem; border: 1px solid #dce2dc; border-radius: .8rem; background: #fafbf9; }
.grid { display: grid; gap: 1rem; }
.grid.two { grid-template-columns: 1fr 1fr; }
.hours { display: grid; gap: .6rem; }
.day-row { display: grid; min-height: 2.5rem; align-items: center; gap: .75rem; grid-template-columns: 7rem 6rem 1fr auto 1fr; text-transform: capitalize; }
.closed { display: flex; align-items: center; gap: .4rem; }
.closed input { width: auto; }
.message { padding: .85rem 1rem; border-radius: .65rem; }
.message.error { color: #86251f; background: #fee9e7; }
.message.success { color: #295b40; background: #e2f1e7; }
.message.info { color: #4c596e; background: #e8edf7; }
.save-bar { padding-top: 1rem; color: #6d7972; font-size: .85rem; }
@media (max-width: 700px) {
  header { position: static; padding: 1rem; align-items: flex-start; gap: 1rem; flex-wrap: wrap; }
  .header-actions { width: 100%; }
  .header-actions > * { flex: 1; justify-content: center; }
  .manager-tabs { position: static; }
  .grid.two, .contact-row { grid-template-columns: 1fr; }
  .day-row { grid-template-columns: 1fr 1fr; padding-block: .5rem; border-bottom: 1px solid #e2e6e3; }
  .save-bar { align-items: stretch; flex-direction: column; }
}
</style>
